/** Akıl yürütme: örüntü, dizi tamamlama, mantık, sudoku, kategori */

import { Rng } from '../../prng'
import {
  CATEGORIES_TR,
  SHAPES_HOLLOW,
  SHAPES_SIMPLE,
  gradeList,
  gradeSingle,
  makeSudoku,
} from '../helpers'
import type { ShapeName } from '../../render/shapes'
import { EXERCISE_VERSION, clampLevel, scale } from '../types'
import type { CellNode, ExerciseTemplate } from '../types'

/* ================================================================== *
 * 11. Matris örüntüsü
 *
 * Satır ve sütun kurallarına göre eksik hücreyi bul.
 *
 * ESKİ HALİ ÇÖZÜLEMEZDİ. Kural `(offset + satır × adım + sütun) mod 6`
 * idi ve 3×3 ızgarada boş kutuya gelecek sembol ızgaranın BAŞKA HİÇBİR
 * YERİNDE geçmiyordu — 1. ve 3. düzeyde 300 sayfanın 300'ünde böyleydi.
 * Çocuğun kuralı çıkarabilmesi için havuzun gizli sırasını ("●'den sonra
 * ■, ondan sonra ▲...") bilmesi gerekiyordu; bilemez. Satır-sütun
 * mantığıyla bakıldığında şıkların dördü birden savunulabilir çıkıyordu.
 * Örnek (3. düzey, tohum 0):
 *
 *     ◆ ♥ ●        şıklar: ★ ▲ ◆ ♥
 *     ♥ ● ■        "doğru" cevap ▲ — ızgarada hiç geçmiyor
 *     ● ■ ?
 *
 * Ayrıca boş kutu HER ZAMAN sağ alttaydı ve kuralın tek serbest
 * parametresi vardı (`offset`, 0–5), yani 10. düzeyde üretilebilen
 * gerçek ızgara sayısı altıydı. Görünen 1601 "farklı sayfa"nın farkı
 * şıkların karışma sırasıydı; şıkları karıştırmak yeni sayfa değil.
 *
 * YENİ KURGU — üç kural ailesi, hepsinde ortak şart: boş kutunun cevabı
 * ızgarada görünen bir şeyden çıkarılabilecek.
 *
 *   satir / sutun : her satır (ya da sütun) tek bir şekil. Cevap aynı
 *                   satırda zaten duruyor. En kolay aile.
 *   latin         : her şekil her satırda ve her sütunda tam bir kez.
 *                   Boş kutu TEK bir şekle zorlanıyor — sudoku mantığı.
 *   ikili         : şekil satırdan, içi dolu/boş olması sütundan gelir.
 *                   İki bağımsız kural aynı anda; en zor aile.
 *
 * Çeşitlilik de buradan geliyor: latin karenin satır sırası, sütun
 * sırası ve şekil ataması ayrı ayrı karışıyor, boş kutu her yere
 * düşebiliyor ve aile tohumla seçiliyor.
 * ================================================================== */

/**
 * Dolu/boş çiftleri: `ikili` ailesinde ikinci özellik bunlar üzerinden.
 *
 * Katman 0'dan sonra çift KURULMUYOR, zaten öyle tanımlı: `SHAPES_SIMPLE`
 * ile `SHAPES_HOLLOW` aynı sırada ve aynı yolu paylaşıyor. Eskiden
 * `●`/`○` fontta boyca da farklıydı; şimdi iki şeklin kenarı birebir
 * çakışıyor, fark yalnızca dolgu.
 */
const CIFTLER: [ShapeName, ShapeName][] = SHAPES_SIMPLE.map((d, i) => [d, SHAPES_HOLLOW[i]!])

/**
 * Latin kare: her değer her satırda ve her sütunda tam bir kez.
 *
 * `(satirSira[r] + sutunSira[c]) % n` döngüsel kareyi veriyor; satır,
 * sütun ve sembol atamasını ayrı ayrı karıştırmak onu aynı aileden
 * çok sayıda farklı kareye açıyor. Eski kurgunun tek `offset`
 * parametresinin yerini bu üç permütasyon alıyor.
 */
function latinKare(rng: Rng, n: number): number[] {
  const satirSira = rng.shuffle(Array.from({ length: n }, (_, i) => i))
  const sutunSira = rng.shuffle(Array.from({ length: n }, (_, i) => i))
  const semboller = rng.shuffle(Array.from({ length: n }, (_, i) => i))
  const kare = new Array<number>(n * n)
  for (let r = 0; r < n; r++) {
    for (let c = 0; c < n; c++) {
      kare[r * n + c] = semboller[(satirSira[r]! + sutunSira[c]!) % n]!
    }
  }
  return kare
}

type Kural = 'satir' | 'sutun' | 'latin' | 'ikili'

export const matrixPattern: ExerciseTemplate = {
  code: 'matrix_pattern',
  title: 'Örüntüyü tamamla',
  domains: ['cognitive_flexibility', 'working_memory_visual'],
  channels: ['digital', 'print'],
  params(level) {
    const l = clampLevel(level)
    /**
     * Zorluk üç yerden geliyor: ızgara boyu, şık sayısı ve kural ailesi.
     * Aile listesi düzeyle daralıyor — alt düzeylerde kolay aileler,
     * üst düzeylerde yalnız zor olanlar.
     */
    const aileler: Kural[] =
      l <= 2
        ? ['satir', 'sutun']
        : l <= 4
          ? ['satir', 'sutun', 'latin']
          : l <= 7
            ? ['latin']
            : ['latin', 'ikili']
    return { size: scale(l, 3, 5), choices: scale(l, 3, 6), aileler: aileler.join(',') }
  },
  generate(seed, level) {
    const p = this.params(level)
    const rng = new Rng(seed)
    const size = p['size'] as number
    const aileler = (p['aileler'] as string).split(',') as Kural[]
    const kural = rng.pick(aileler)

    /** Izgaradaki şekiller ve kullanılan evren */
    let glifler: ShapeName[]
    let evren: ShapeName[]

    if (kural === 'ikili') {
      // Şekil satırdan, dolu/boş sütundan. İki kural bağımsız: çocuk
      // ikisini ayrı ayrı bulup birleştirmek zorunda.
      const cift = rng.sample(CIFTLER, Math.min(size, CIFTLER.length))
      const dolusuz = rng.bool() // sütun deseni ters çevrilebiliyor
      glifler = []
      for (let r = 0; r < size; r++) {
        for (let c = 0; c < size; c++) {
          const ic = (c % 2 === 0) === dolusuz ? 1 : 0
          glifler.push(cift[r % cift.length]![ic]!)
        }
      }
      evren = [...new Set(glifler)]
    } else if (kural === 'latin') {
      const semboller = rng.sample(SHAPES_SIMPLE, size)
      const kare = latinKare(rng, size)
      glifler = kare.map((i) => semboller[i]!)
      evren = [...semboller]
    } else {
      // satir: her satır tek şekil — sutun: her sütun tek şekil
      const semboller = rng.sample(SHAPES_SIMPLE, size)
      glifler = []
      for (let r = 0; r < size; r++) {
        for (let c = 0; c < size; c++) {
          glifler.push(semboller[kural === 'satir' ? r : c]!)
        }
      }
      evren = [...semboller]
    }

    /**
     * Boş kutu her yere düşebiliyor.
     *
     * Eskiden hep sağ alttaydı; çocuk kuralı değil yeri öğreniyordu.
     * Her üç ailede de cevap satırdan ya da sütundan çıkarılabildiği
     * için konumun serbest olması bulmacayı çözümsüz bırakmıyor.
     */
    const bos = rng.int(0, size * size - 1)
    const dogru = glifler[bos]!

    const cells: CellNode[] = glifler.map((g, i) =>
      i === bos ? { blank: true, id: 'missing' } : { shape: g },
    )

    // Şıklar ızgarada GEÇEN gliflerden: evren dışından bir şık koymak
    // "hiç görmediğim şekil, demek ki yanlış" kısayolunu açardı
    const yanlislar = rng.shuffle(evren.filter((g) => g !== dogru))
    const choiceCount = Math.min(p['choices'] as number, evren.length)
    const options = rng.shuffle([dogru, ...yanlislar.slice(0, choiceCount - 1)])

    return {
      code: 'matrix_pattern',
      version: EXERCISE_VERSION,
      level: clampLevel(level),
      seed: String(seed),
      // Hangi ailenin çıktığı örnekte duruyor: uygulayıcı aynı düzeyde
      // niye farklı güçlükte sayfalar gördüğünü görebilsin
      params: { ...p, kural },
      domains: this.domains,
      prompt: 'Tabloda eksik olan kutuya hangisi gelmeli?',
      render: [
        { t: 'grid', cols: size, rows: size, cells, border: true },
        { t: 'choices', items: options.map((g, i) => ({ id: 'o' + i, shape: g })) },
      ],
      solution: 'o' + options.indexOf(dogru),
      grading: 'auto',
      estimatedSeconds: 40,
      itemCount: 1,
    }
  },
  grade(instance, answer) {
    return gradeSingle(instance.solution, answer)
  },
}

/* ================================================================== *
 * 12. Sayı dizisi tamamlama
 *
 * Kural üretilir: aritmetik, geometrik veya değişken adım. Çözüm
 * kuralın kendisinden hesaplanır, elle yazılmaz.
 * ================================================================== */
export const sequenceComplete: ExerciseTemplate = {
  code: 'sequence_complete',
  title: 'Diziyi tamamla',
  domains: ['working_memory_verbal', 'cognitive_flexibility'],
  channels: ['digital', 'print'],
  params(level) {
    const l = clampLevel(level)
    return { items: scale(l, 4, 9), rows: scale(l, 4, 8), kinds: l >= 7 ? 3 : l >= 4 ? 2 : 1 }
  },
  generate(seed, level) {
    const p = this.params(level)
    const rng = new Rng(seed)
    const len = p['items'] as number
    const rows = p['rows'] as number
    const kinds = p['kinds'] as number

    const lines: { label: string; answer: string }[] = []
    for (let r = 0; r < rows; r++) {
      const kind = rng.int(0, kinds - 1)
      const start = rng.int(1, 12)
      let seq: number[]
      if (kind === 0) {
        const d = rng.int(2, 9)
        seq = Array.from({ length: len }, (_, i) => start + i * d)
      } else if (kind === 1) {
        const m = rng.int(2, 3)
        seq = Array.from({ length: len }, (_, i) => start * m ** i)
      } else {
        // Artan adım: +1, +2, +3 ...
        const d0 = rng.int(1, 4)
        seq = [start]
        for (let i = 1; i < len; i++) seq.push(seq[i - 1]! + d0 + i - 1)
      }
      const shown = seq.slice(0, len - 1).join(', ')
      lines.push({ label: `${shown}, ___`, answer: String(seq[len - 1]) })
    }

    return {
      code: 'sequence_complete',
      version: EXERCISE_VERSION,
      level: clampLevel(level),
      seed: String(seed),
      params: p,
      domains: this.domains,
      prompt: 'Her dizide kuralı bul ve son sayıyı yaz.',
      render: [
        { t: 'rows', items: lines.map((l, i) => ({ label: l.label, blank: true, id: 's' + i })) },
      ],
      solution: lines.map((l) => l.answer),
      grading: 'auto',
      estimatedSeconds: rows * 25,
      itemCount: rows,
    }
  },
  grade(instance, answer) {
    return gradeList(instance.solution as string[], answer)
  },
}

/* ================================================================== *
 * 13. Mantık bulmacası
 *
 * "Ali en uzun değil, Ayşe Ali'den kısa" türü. İpuçları ÇÖZÜMDEN
 * türetilir, yani her bulmacanın bir cevabı olduğu garanti.
 * ================================================================== */
export const logicGrid: ExerciseTemplate = {
  code: 'logic_grid',
  title: 'Sıralamayı bul',
  domains: ['working_memory_verbal', 'cognitive_flexibility'],
  channels: ['digital', 'print'],
  params(level) {
    const l = clampLevel(level)
    return { people: scale(l, 3, 6), clues: scale(l, 2, 5) }
  },
  generate(seed, level) {
    const p = this.params(level)
    const rng = new Rng(seed)
    const count = p['people'] as number
    const names = rng.sample(
      ['Ali', 'Ayse', 'Mert', 'Zeynep', 'Can', 'Elif', 'Deniz', 'Kaan'],
      count,
    )
    // Gerçek sıralama (en kısadan en uzuna)
    const order = rng.shuffle(names)

    const clues: string[] = []
    // Ardışık ikililerden ipucu: çözümden türetildiği için tutarlı
    for (let i = 0; i < Math.min(p['clues'] as number, count - 1); i++) {
      const a = order[i]!
      const b = order[i + 1]!
      clues.push(`${b}, ${a}'dan uzun.`)
    }
    if ((p['clues'] as number) > count - 1) {
      clues.push(`${order[order.length - 1]} hepsinden uzun.`)
    }
    /*
     * İPUÇLARI KARIŞTIRILIYOR.
     *
     * Türetme sırası çözümün sırasıdır: ipuçları baştan sona okununca
     * sıralamayı doğrudan veriyordu ("B, A'dan uzun. C, B'den uzun.
     * D, C'den uzun."). Sayfa üretiliyordu, puanlama doğru çalışıyordu
     * ve ölçtüğü şey sıralama değil kopyalamaydı. `zebra_grid`
     * yazılırken fark edildi.
     *
     * Sürüm artmıyor çünkü bu şablonun ürettiği SAYFA aynı — aynı
     * ipuçları, başka sırada. Eski bir atama hâlâ aynı soruyu soruyor
     * ve aynı cevabı istiyor.
     */
    const karisikClues = rng.shuffle(clues)

    return {
      code: 'logic_grid',
      version: EXERCISE_VERSION,
      level: clampLevel(level),
      seed: String(seed),
      params: p,
      domains: this.domains,
      prompt: 'İpuçlarını oku ve en kısadan en uzuna doğru sırala.',
      render: [
        { t: 'text', value: karisikClues.join(' '), size: 'md' },
        {
          t: 'rows',
          items: order.map((_, i) => ({ label: `${i + 1}.`, blank: true, id: 'p' + i })),
        },
      ],
      solution: order,
      grading: 'auto',
      estimatedSeconds: count * 25,
      itemCount: count,
    }
  },
  grade(instance, answer) {
    return gradeList(instance.solution as string[], answer)
  },
}

/* ================================================================== *
 * 14. Mini sudoku
 *
 * 4×4 veya 6×6. Üretici TEK ÇÖZÜMLÜ olduğunu kanıtlamadan sayfayı
 * teslim etmez (bkz. helpers/makeSudoku).
 * ================================================================== */
export const sudokuMini: ExerciseTemplate = {
  code: 'sudoku_mini',
  title: 'Mini sudoku',
  domains: ['working_memory_visual', 'cognitive_flexibility'],
  channels: ['digital', 'print'],
  params(level) {
    const l = clampLevel(level)
    return { size: l >= 6 ? 6 : 4, remove: l >= 6 ? scale(l, 14, 22) : scale(l, 5, 10) }
  },
  generate(seed, level) {
    const p = this.params(level)
    const rng = new Rng(seed)
    const size = p['size'] as 4 | 6
    const { puzzle, solution } = makeSudoku(rng, size, p['remove'] as number)

    const cells: CellNode[] = puzzle.map((v, i) =>
      v === 0 ? { blank: true, id: 'c' + i } : { glyph: String(v), marked: true },
    )

    return {
      code: 'sudoku_mini',
      version: EXERCISE_VERSION,
      level: clampLevel(level),
      seed: String(seed),
      params: p,
      domains: this.domains,
      // Türkçe yönelme eki sayıya göre değişir: 4'E ama 6'YA.
      // Şablonla "4'ya" yazıyordu.
      prompt:
        `Her satırda, her sütunda ve her bölgede 1'den ${size === 4 ? "4'e" : "6'ya"} ` +
        `kadar sayılar birer kez olsun.`,
      render: [{ t: 'grid', cols: size, rows: size, cells, border: true }],
      solution: { puzzle, solution },
      grading: 'auto',
      estimatedSeconds: size === 4 ? 120 : 300,
      itemCount: puzzle.filter((v) => v === 0).length,
    }
  },
  /**
   * Anahtar: çözüm ızgarası, satır satır.
   *
   * Ham JSON iki diziyi birden basıyordu (bulmaca + çözüm) ve
   * uygulayıcı hangisinin hangisi olduğunu ayırt edemiyordu.
   */
  anahtar(instance) {
    const { solution } = instance.solution as { solution: number[] }
    const size = instance.params['size'] as number
    const satirlar: string[] = []
    for (let r = 0; r < size; r++) {
      satirlar.push(solution.slice(r * size, r * size + size).join(' '))
    }
    return satirlar.join('  /  ')
  },
  grade(instance, answer) {
    const { puzzle, solution } = instance.solution as { puzzle: number[]; solution: number[] }
    const got = (answer ?? {}) as Record<string, unknown>
    const blanks = puzzle.map((v, i) => (v === 0 ? i : -1)).filter((i) => i >= 0)
    const perItem = blanks.map((i) => Number(got['c' + i]) === solution[i])
    const correct = perItem.filter(Boolean).length
    return {
      correct,
      total: blanks.length,
      accuracy: blanks.length ? correct / blanks.length : 0,
      perItem,
      items: blanks.map((i) => {
        const ham = got['c' + i]
        return {
          id: 'c' + i,
          beklenen: String(solution[i]),
          verilen: ham === undefined || ham === null || String(ham) === '' ? null : String(ham),
          dogru: Number(ham) === solution[i],
        }
      }),
    }
  },
}

/* ================================================================== *
 * 15. Kategoriye ayırma
 * ================================================================== */
export const categorySort: ExerciseTemplate = {
  code: 'category_sort',
  title: 'Gruplara ayır',
  domains: ['cognitive_flexibility', 'working_memory_verbal'],
  channels: ['digital', 'print'],
  params(level) {
    const l = clampLevel(level)
    return { categories: scale(l, 2, 4), perCategory: scale(l, 3, 6) }
  },
  generate(seed, level) {
    const p = this.params(level)
    const rng = new Rng(seed)
    const catCount = p['categories'] as number
    const per = p['perCategory'] as number

    const names = rng.sample(Object.keys(CATEGORIES_TR), catCount)
    const items: { word: string; cat: string }[] = []
    for (const c of names) {
      for (const w of rng.sample(CATEGORIES_TR[c]!, Math.min(per, CATEGORIES_TR[c]!.length))) {
        items.push({ word: w, cat: c })
      }
    }
    const shuffled = rng.shuffle(items)

    return {
      code: 'category_sort',
      version: EXERCISE_VERSION,
      level: clampLevel(level),
      seed: String(seed),
      params: p,
      domains: this.domains,
      prompt: `Kelimeleri gruplara ayır: ${names.join(', ')}.`,
      render: [
        {
          t: 'rows',
          items: shuffled.map((it, i) => ({ label: it.word, blank: true, id: 'w' + i })),
          columns: 3,
          // Grup adları paletten seçiliyor: elle yazmak imla hatasına
          // açık ve ölçtüğümüz şey imla değil
          palette: names.map((n) => ({ glyph: n })),
        },
      ],
      solution: shuffled.map((it) => it.cat),
      grading: 'auto',
      estimatedSeconds: shuffled.length * 6,
      itemCount: shuffled.length,
    }
  },
  grade(instance, answer) {
    return gradeList(instance.solution as string[], answer)
  },
}
